import { motion } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { X, Check, Mic, Image as ImageIcon } from "lucide-react";

const TODAY = [
  "Opportunities buried beneath hundreds of unread messages",
  "Counterparties you've never met — and no way to vet them",
  "Splits agreed over voice notes, with no written record to stand on",
  "Your reach ends at your own contact list",
];

const PRIMECONNECT = [
  "Every post structured, searchable and matched to you",
  "Every member verified against their agency",
  "Splits agreed in writing, in-app, before any introduction",
  "Verified prime agents worldwide, on demand",
];

const THREAD = [
  { from: "Olivia G. · Belgravia & Co", time: "09:02", text: "Anyone got a 2+ bed in Mayfair? Client ready to move. £8–15k/mo." },
  { from: "Marco P.", time: "09:07", photo: true },
  { from: "James H.", time: "09:07", text: "Happy birthday Marco!!" },
  { from: "Sofia B.", time: "09:11", voice: "0:47" },
  { from: "Tom W.", time: "09:26", text: "Sorry — what was the budget again?" },
  { from: "Harry F. · Chestertons", time: "09:58", text: "I think I have something. DM me." },
  { from: "Olivia G. · Belgravia & Co", time: "14:30", text: "Client went with a portal flat in the end.", dim: true },
];

const RESCUED = [
  { time: "09:14", label: "Client search posted", detail: "2+ bed apartment · Mayfair · £8,000–£15,000/mo" },
  { time: "09:31", label: "Matched automatically", detail: "2 bed lateral flat, Mount Street — Harry F., Chestertons" },
  { time: "09:41", label: "Split agreed in writing", detail: "50 / 50 · confirmed in-app before any names were exchanged" },
  { time: "10:05", label: "Introduction made", detail: "Client details shared only when Olivia chose to share them" },
];

const threadContainer = {
  hidden: {},
  show: { transition: { staggerChildren: 0.55, delayChildren: 0.35 } },
};

const threadItem = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } },
};

export const WhyPrimeConnect = () => (
  <section
    id="why-primeconnect"
    data-testid="why-primeconnect-section"
    className="py-24 sm:py-32 bg-[#141518] text-[#FAF9F5]"
  >
    <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
      <Reveal>
        <p className="font-eyebrow text-[11px] text-[#B89B6C]">Why PrimeConnect</p>
        <h2 className="font-display font-medium tracking-tight text-4xl sm:text-5xl mt-5 leading-[1.08] max-w-3xl">
          Prime deals don&rsquo;t belong in <em className="italic font-light">a group chat.</em>
        </h2>
        <p className="text-base sm:text-lg text-[#FAF9F5]/55 leading-relaxed mt-6 max-w-2xl">
          Splitting commission with another agent is nothing new — it&rsquo;s
          standard practice. What&rsquo;s new is doing it without the chaos. Today
          these deals live in WhatsApp groups and contact books: unstructured,
          unverified, and limited to whoever you already happen to know.
          PrimeConnect keeps the practice, removes the friction, and stretches
          your network worldwide.
        </p>
      </Reveal>

      <div className="mt-16 sm:mt-20 grid lg:grid-cols-2 gap-6">
        <Reveal>
          <div
            data-testid="why-today-card"
            className="h-full rounded-3xl border border-[#FAF9F5]/10 p-8 sm:p-10"
          >
            <p className="font-eyebrow text-[10px] text-[#FAF9F5]/40">The group chat, today</p>
            <div className="mt-8 space-y-6">
              {TODAY.map((t) => (
                <div key={t} className="flex gap-3.5 items-start">
                  <X size={16} className="text-[#FAF9F5]/35 mt-1 shrink-0" strokeWidth={1.5} />
                  <p className="text-sm text-[#FAF9F5]/55 leading-relaxed">{t}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div
            data-testid="why-primeconnect-card"
            className="h-full rounded-3xl bg-[#FAF9F5] text-[#141518] p-8 sm:p-10"
          >
            <p className="font-eyebrow text-[10px] text-[#B89B6C]">PrimeConnect</p>
            <div className="mt-8 space-y-6">
              {PRIMECONNECT.map((t) => (
                <div key={t} className="flex gap-3.5 items-start">
                  <Check size={16} className="text-[#B89B6C] mt-1 shrink-0" strokeWidth={1.5} />
                  <p className="text-sm text-[#141518]/75 leading-relaxed">{t}</p>
                </div>
              ))}
            </div>
            <p className="mt-10 pt-8 border-t border-[#E7E4DC] font-display italic text-xl text-[#141518]">
              &ldquo;Same deals you already do — structured, verified, and ten
              times the reach.&rdquo;
            </p>
          </div>
        </Reveal>
      </div>

      <div className="mt-24 sm:mt-28">
        <Reveal>
          <p className="font-eyebrow text-[11px] text-[#B89B6C]">A true story, anonymised</p>
          <h3 className="font-display font-medium tracking-tight text-3xl sm:text-4xl mt-4 leading-[1.1]">
            The deal that almost got away.
          </h3>
        </Reveal>

        <div className="mt-12 grid lg:grid-cols-2 gap-6">
          <motion.div
            variants={threadContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-120px" }}
          >
            <div
              data-testid="story-before-card"
              className="h-full rounded-3xl border border-[#FAF9F5]/10 p-6 sm:p-8"
            >
              <div className="flex items-baseline justify-between">
                <p className="font-eyebrow text-[10px] text-[#FAF9F5]/40">Before · the buried thread</p>
                <p className="font-eyebrow text-[10px] text-[#FAF9F5]/30">Prime London Lets · 214 members</p>
              </div>
              <div className="mt-7 space-y-3">
                {THREAD.map((m, i) => (
                  <motion.div
                    key={i}
                    variants={threadItem}
                    className={`relative rounded-2xl px-4 py-3 max-w-[88%] ${
                      m.dim
                        ? "bg-[#FAF9F5]/10 border border-[#FAF9F5]/10"
                        : "bg-[#FAF9F5]/[0.05]"
                    }`}
                  >
                    {i === 0 && (
                      <motion.span
                        data-testid="story-opportunity-marker"
                        initial={{ opacity: 1 }}
                        whileInView={{ opacity: 0.12 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.35 + THREAD.length * 0.55, duration: 1.4 }}
                        className="absolute left-0 top-2.5 bottom-2.5 w-[2px] rounded-full bg-[#B89B6C]"
                      />
                    )}
                    <div className="flex items-baseline justify-between gap-4">
                      <p className="font-eyebrow text-[9px] text-[#B89B6C]/80">{m.from}</p>
                      <p className="font-eyebrow text-[9px] text-[#FAF9F5]/30 shrink-0">{m.time}</p>
                    </div>
                    {m.photo ? (
                      <div className="mt-2 h-16 rounded-xl bg-[#FAF9F5]/10 flex items-center justify-center gap-2">
                        <ImageIcon size={14} className="text-[#FAF9F5]/40" />
                        <span className="text-xs text-[#FAF9F5]/40">Photo</span>
                      </div>
                    ) : m.voice ? (
                      <div className="mt-2 flex items-center gap-2.5">
                        <Mic size={13} className="text-[#FAF9F5]/40 shrink-0" />
                        <div className="flex items-end gap-[3px] h-4">
                          {[6, 12, 8, 14, 5, 10, 13, 7, 11, 4, 9, 6].map((h, j) => (
                            <span
                              key={j}
                              className="w-[2px] rounded-full bg-[#FAF9F5]/30"
                              style={{ height: `${h}px` }}
                            />
                          ))}
                        </div>
                        <span className="text-xs text-[#FAF9F5]/40">{m.voice}</span>
                      </div>
                    ) : (
                      <p className={`text-[13px] leading-relaxed mt-1.5 ${m.dim ? "text-[#FAF9F5]/70" : "text-[#FAF9F5]/60"}`}>
                        {m.text}
                      </p>
                    )}
                  </motion.div>
                ))}
              </div>
              <p className="mt-7 pt-5 border-t border-[#FAF9F5]/10 font-display italic text-lg text-[#FAF9F5]/50">
                47 messages. Zero follow-ups. One lost deal.
              </p>
            </div>
          </motion.div>

          <Reveal delay={0.15}>
            <div
              data-testid="story-after-card"
              className="h-full rounded-3xl bg-[#FAF9F5] text-[#141518] p-6 sm:p-8"
            >
              <div className="flex items-baseline justify-between">
                <p className="font-eyebrow text-[10px] text-[#B89B6C]">After · the same search on PrimeConnect</p>
              </div>
              <div className="mt-7">
                {RESCUED.map((s, i) => (
                  <div
                    key={s.time}
                    className={`flex gap-5 py-5 ${i < RESCUED.length - 1 ? "border-b border-[#E7E4DC]" : ""}`}
                  >
                    <span className="font-eyebrow text-[10px] text-[#B89B6C] pt-1 shrink-0 w-10">
                      {s.time}
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-[#141518]">{s.label}</p>
                      <p className="text-[13px] text-[#635F57] leading-relaxed mt-1">{s.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-7 pt-5 border-t border-[#E7E4DC] font-display italic text-lg text-[#141518]">
                27 minutes. One rescued deal.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);
